import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { MarkdownContent } from '@/components/markdown-content'
import { getMyPollResultRows } from '@/components/polls/poll-result-rows'
import { PollResults } from '@/components/polls/poll-results'
import { PollVoteForm } from '@/components/polls/poll-vote-form'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getMyPendingAnnouncements } from '@/lib/announcements-api'
import { getCurrentUserRole } from '@/lib/api'
import { formatDateTime } from '@/lib/date'
import { getErrorMessage } from '@/lib/error-messages'
import { dismissPoll, getMyPolls } from '@/lib/polls-api'
import { queryKeys } from '@/lib/query-keys'

// Shows open polls the user has neither voted on nor closed, one after another.
// Closing stores a dismissal, so the poll is only reachable on the polls page.
export function PollDialog() {
  const profileQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
    retry: false,
    refetchOnWindowFocus: false,
  })
  const profile = profileQuery.data

  if (!profile?.is_active || profile.role === 'admin') return null
  // Keyed per user so handled polls never leak across an account switch.
  return (
    <PendingPollsDialog
      key={profile.id}
      userId={profile.id}
      pollsPath={profile.role === 'trainer' ? '/trainer/umfragen' : '/umfragen'}
    />
  )
}

function PendingPollsDialog({ userId, pollsPath }: { userId: string; pollsPath: string }) {
  const location = useLocation()
  const queryClient = useQueryClient()
  const [handledIds, setHandledIds] = useState<string[]>([])
  // Set when voting starts, so the refetch after the vote switches to the
  // results of this poll instead of jumping to the next one.
  const [activeId, setActiveId] = useState<string | null>(null)

  // Same query as the announcement dialog: announcements are shown first.
  const announcementsQuery = useQuery({
    queryKey: [...queryKeys.myPendingAnnouncements, userId],
    queryFn: getMyPendingAnnouncements,
  })
  const announcementsDone = announcementsQuery.isSuccess
    ? announcementsQuery.data.length === 0
    : announcementsQuery.isError

  const pollsQuery = useQuery({
    queryKey: queryKeys.myPolls,
    queryFn: getMyPolls,
  })
  const polls = pollsQuery.data ?? []
  const pending = polls.filter(
    (poll) => poll.is_open && !poll.has_voted && !poll.dismissed && !handledIds.includes(poll.id),
  )
  const active = polls.find((poll) => poll.id === activeId) ?? null
  const canShow = announcementsDone && location.pathname !== pollsPath
  const current = canShow ? (active ?? pending[0] ?? null) : null

  const finish = (id: string) => {
    setHandledIds((ids) => [...ids, id])
    setActiveId(null)
  }

  const dismissMutation = useMutation({
    mutationFn: dismissPoll,
    onSuccess: (_, id) => {
      finish(id)
      queryClient.invalidateQueries({ queryKey: queryKeys.myPolls })
    },
    onError: (error, id) => {
      if (error.message !== 'POLL_NOT_AVAILABLE') {
        toast.error(getErrorMessage(error))
        return
      }
      // Withdrawn in the meantime: just move on.
      finish(id)
      queryClient.invalidateQueries({ queryKey: queryKeys.myPolls })
    },
  })

  const resultRows = current?.has_voted ? getMyPollResultRows(current) : null
  const close = () => {
    if (!current) return
    if (current.has_voted) finish(current.id)
    else dismissMutation.mutate(current.id)
  }

  return (
    <Dialog open={Boolean(current)} onOpenChange={(open) => !open && close()}>
      <DialogContent
        onInteractOutside={(event) => event.preventDefault()}
        className="max-h-[90vh] max-w-lg overflow-y-auto"
      >
        {current && (
          <>
            <DialogHeader>
              <DialogTitle>{current.title}</DialogTitle>
              <DialogDescription>
                {current.has_voted
                  ? 'Danke für deine Stimme!'
                  : [
                      pending.length > 1 ? `Umfrage 1 von ${pending.length}` : 'Umfrage',
                      current.visible_until ? `offen bis ${formatDateTime(current.visible_until)}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
              </DialogDescription>
            </DialogHeader>
            {current.has_voted ? (
              <>
                {resultRows && <PollResults rows={resultRows} voterCount={current.voter_count ?? 0} />}
                <DialogFooter>
                  <Button type="button" onClick={() => finish(current.id)} autoFocus>
                    Schließen
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <div className="max-h-[40vh] overflow-y-auto">
                  <MarkdownContent>{current.body}</MarkdownContent>
                </div>
                <PollVoteForm
                  poll={current}
                  onVoteStart={() => setActiveId(current.id)}
                  onVoted={() => {
                    if (current.results_visible) return
                    toast.success('Danke für deine Stimme!')
                    finish(current.id)
                  }}
                  actions={
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={close}
                      disabled={dismissMutation.isPending}
                    >
                      Schließen
                    </Button>
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Geschlossene Umfragen findest du jederzeit unter{' '}
                  <Link to={pollsPath} className="underline underline-offset-2">
                    Umfragen
                  </Link>
                  .
                </p>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
